import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Quote, Award, Building2, Ruler, Clock } from "lucide-react";
import { HardHat, DraftingCompass, Sofa } from "lucide-react";
import Seo from "@/components/Seo";
import CostEstimator from "@/components/CostEstimator";
import { useLang } from "@/i18n/LanguageContext";
import { BUSINESS, IMAGES, SERVICES, PROJECTS, TESTIMONIALS, STATS, SERVICE_ACCENTS, pick } from "@/data/site";

export const ICONS = { HardHat, DraftingCompass, Building2, Sofa, Ruler };

const STAT_STYLES = [
  { bg: "bg-orange-50 border-orange-200", text: "text-orange-600", iconBg: "bg-orange-500" },
  { bg: "bg-teal-50 border-teal-200", text: "text-teal-700", iconBg: "bg-teal-600" },
  { bg: "bg-amber-50 border-amber-200", text: "text-amber-600", iconBg: "bg-amber-500" },
  { bg: "bg-blue-50 border-blue-200", text: "text-blue-700", iconBg: "bg-blue-600" },
];
const STAT_ICONS = [Award, Building2, Ruler, Clock];

const STATUS_PILL = {
  Completed: "bg-emerald-500 text-white",
  "In Progress": "bg-amber-500 text-white",
  Upcoming: "bg-blue-500 text-white",
};

const TESTI_ACCENTS = ["text-orange-300", "text-teal-300", "text-amber-300"];

export default function Home() {
  const { t, lang } = useLang();
  const featured = PROJECTS.slice(0, 3);
  return (
    <div data-testid="home-page">
      <Seo
        title="V.B. Sharada Associates — Contractors, Building Planning & Interiors in Tandur"
        description={BUSINESS.description}
      />

      <section className="warm-grid border-b border-orange-100" data-testid="hero-section">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              <span className="w-2 h-2 rounded-full bg-teal-500" />
              {t("home.eyebrow")}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08]">
              {t("home.h1a")}
              <span className="text-gradient-warm">{t("home.h1b")}</span>
              {t("home.h1c")}
            </h1>
            <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-xl">{t("home.heroPara")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-orange-600 transition-colors duration-200 shadow-lg shadow-orange-500/30"
              >
                {t("home.consultBtn")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/projects"
                data-testid="hero-projects-button"
                className="inline-flex items-center justify-center gap-2 border-2 border-teal-600 text-teal-700 font-semibold px-7 py-3.5 rounded-full hover:bg-teal-50 transition-colors duration-200"
              >
                {t("home.projectsBtn")}
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl bg-gradient-to-br from-orange-200 via-amber-100 to-teal-100" />
            <img
              src={IMAGES.hero}
              alt="Modern residential building constructed by V.B. Sharada Associates in Tandur"
              className="relative rounded-3xl shadow-xl w-full h-[320px] sm:h-[420px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white border border-orange-100 shadow-lg rounded-2xl px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <BadgeCheck className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <div className="font-heading font-bold text-slate-900 text-sm">{t("home.badgeTitle")}</div>
                <div className="text-xs text-slate-500">{t("home.badgeSub")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-orange-100 bg-white/70 backdrop-blur">
          <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-bar">
            {STATS.map((s, i) => {
              const st = STAT_STYLES[i % STAT_STYLES.length];
              const Icon = STAT_ICONS[i % STAT_ICONS.length];
              return (
                <div key={s.label} className={`rounded-2xl border ${st.bg} p-4 sm:p-5 flex items-center gap-3 sm:gap-4`}>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl ${st.iconBg} text-white flex items-center justify-center shrink-0 shadow-md`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <div className={`font-heading text-2xl sm:text-3xl font-extrabold ${st.text}`}>{s.value}</div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-0.5">{pick(s, "label", lang)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="services-teaser">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                {t("home.servicesEyebrow")}
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                {t("home.servicesH2")}
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all-link"
              className="inline-flex items-center gap-2 text-orange-600 font-semibold text-sm hover:gap-3 transition-all duration-200"
            >
              {t("home.allServices")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              const accent = SERVICE_ACCENTS[i % SERVICE_ACCENTS.length];
              return (
                <Link
                  to="/services"
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={IMAGES[s.image]}
                      alt={`${s.title} — V.B. Sharada Associates, Tandur`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                    <div className={`absolute bottom-3 left-3 w-11 h-11 rounded-xl ${accent.chip} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className={`h-1 w-10 rounded-full bg-gradient-to-r ${accent.bar} mb-3`} />
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{pick(s, "title", lang)}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{pick(s, "short", lang)}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-orange-600 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {t("home.learnMore")} <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
            <div className="bg-slate-900 rounded-2xl p-6 flex flex-col justify-between text-white blueprint-grid-dark overflow-hidden relative">
              <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-orange-500/20 blur-2xl" />
              <div>
                <h3 className="font-heading text-lg font-semibold mb-2">{t("home.helpTitle")}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{t("home.helpText")}</p>
              </div>
              <Link
                to="/contact"
                data-testid="services-teaser-cta"
                className="mt-6 inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-orange-600 transition-colors duration-200 self-start shadow-md shadow-orange-500/30"
              >
                {t("home.helpBtn")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white border-y border-orange-100" data-testid="featured-projects">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
                {t("home.featuredEyebrow")}
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                {t("home.featuredH2")}
              </h2>
            </div>
            <Link
              to="/projects"
              data-testid="projects-view-all-link"
              className="inline-flex items-center gap-2 text-orange-600 font-semibold text-sm hover:gap-3 transition-all duration-200"
            >
              {t("home.allProjects")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <Link
                to="/projects"
                key={p.id}
                data-testid={`featured-project-${p.id}`}
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={IMAGES[p.image]}
                    alt={`${p.title} — ${p.category} project in ${p.location}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow ${STATUS_PILL[p.status]}`}>
                    {t(`status.${p.status}`)}
                  </span>
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-orange-600 mb-2">
                    {t(`cat.${p.category}`)}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{p.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {t(`loc.${p.location}`)} · {pick(p, "scale", lang)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="estimator-section">
        <div className="px-4 sm:px-8 lg:px-12 max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              {t("home.estEyebrow")}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t("home.estH2")}
            </h2>
          </div>
          <CostEstimator />
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white border-y border-orange-100" data-testid="testimonials-section">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              {t("home.testiEyebrow")}
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t("home.testiH2")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tst, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i}`}
                className="bg-orange-50/40 border border-orange-100 rounded-2xl p-6 flex flex-col shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <Quote className={`w-8 h-8 ${TESTI_ACCENTS[i % TESTI_ACCENTS.length]} mb-4`} />
                <blockquote className="text-sm text-slate-600 leading-relaxed flex-1">"{pick(tst, "quote", lang)}"</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-orange-100">
                  <div className="font-heading font-semibold text-slate-900 text-sm">{pick(tst, "name", lang)}</div>
                  <div className="text-xs text-slate-500">{pick(tst, "role", lang)}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-r from-orange-600 via-amber-600 to-teal-600" data-testid="cta-band">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              {t("home.ctaH2")}
            </h2>
            <p className="mt-3 text-orange-50 max-w-xl text-sm sm:text-base">{t("home.ctaText")}</p>
          </div>
          <Link
            to="/contact"
            data-testid="cta-band-button"
            className="inline-flex items-center gap-2 bg-white text-orange-600 font-semibold px-7 py-3.5 rounded-full hover:bg-orange-50 transition-colors duration-200 shrink-0 shadow-lg"
          >
            {t("home.ctaBtn")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
